<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import * as Sentry from '@sentry/vue';
import { useOrderEditStore } from '@/stores/orderEdit';
import { editErrorText, fillText, ruleErrorText, useEditTexts } from '@/composables/useEditTexts';
import { formatCents } from '@/services/orderEditRules';
import { handOffToCheckout } from '@/services/checkoutHandoff';
import EditContact from './edit-contact.vue';

const store = useOrderEditStore();
const t = useEditTexts();

// Set when the embedding page ignored the hand-off (#232): a plain link to
// the same Stripe session. Component state, not store state: the store's
// state goes to Sentry.
const checkoutFallbackUrl = ref<string | null>(null);
let cancelHandoff: (() => void) | null = null;
onBeforeUnmount(() => cancelHandoff?.());

// Spec §9.1: « À payer maintenant », « À rembourser après votre venue » or « Aucun montant à régler ».
const amount = computed(() => {
  const q = store.quote;
  if (!q) return null;
  if (q.netCents > 0) return fillText(t.edit_to_pay, { amount: formatCents(q.netCents) });
  if (q.netCents < 0) return fillText(t.edit_to_refund, { amount: formatCents(q.netCents) });
  return t.edit_nothing_due;
});
const amountState = computed(() => {
  const q = store.quote;
  if (!q) return '';
  return q.netCents > 0 ? 'pay' : q.netCents < 0 ? 'refund' : 'none';
});
const ruleError = computed(() =>
  store.quoteError ? ruleErrorText(t, store.quoteError.code, store.labelOf(store.quoteError.product)) : null,
);
const buttonLabel = computed(() => {
  if (store.submitState === 'redirecting') return t.edit_redirecting;
  return store.quoteChanged ? t.edit_confirm_again : t.edit_validate;
});

async function validate() {
  const result = await store.submit();
  if (result?.status !== 'payment_required') return;
  const url = result.checkoutUrl;
  // postMessage({action:'iframeLocation'}) first; /votre-soiree-approche/ has
  // no handler for it, so the #232 fallback is what reaches Stripe there.
  cancelHandoff = handOffToCheckout(url, {
    onFallback: (topNavigation, error) => {
      checkoutFallbackUrl.value = url;
      // Never the session URL, nor the browser's message (it names it).
      Sentry.captureMessage('Checkout hand-off ignored by the embedding page', {
        level: 'warning',
        tags: { checkout_handoff: 'fallback', top_navigation: topNavigation, mode: 'order_edit' },
        extra: {
          error_name: error instanceof Error ? error.name : undefined,
          visibility: document.visibilityState,
        },
      });
    },
  });
}
</script>

<template>
  <div class="order-edit-summary" data-testid="order-edit-summary" :data-amount-state="amountState">
    <div class="bpa-front-module-heading">{{ t.edit_summary_title }}</div>
    <template v-if="store.deadlinePassed">
      <p class="order-edit-summary__error" role="alert" data-testid="order-edit-deadline">{{ t.edit_error_deadline_passed }}</p>
      <edit-contact />
    </template>
    <template v-else>
      <p v-if="store.pendingChanges.length === 0" data-testid="order-edit-summary-empty">{{ t.edit_summary_empty }}</p>
      <ul v-else class="order-edit-summary__changes">
        <li v-for="change in store.pendingChanges" :key="change.product" data-testid="order-edit-change">
          {{ fillText(t.edit_change_line, { label: change.label, from: change.from, to: change.to }) }}
        </li>
      </ul>
      <p v-if="store.quoteState === 'pending'" data-testid="order-edit-quote-pending">{{ t.edit_summary_computing }}</p>
      <p v-if="ruleError" class="order-edit-summary__error" role="alert" data-testid="order-edit-rule-error">{{ ruleError }}</p>
      <p v-if="store.actionError" class="order-edit-summary__error" role="alert" data-testid="order-edit-action-error">
        {{ editErrorText(t, store.actionError) }}
      </p>
      <p v-if="store.quoteChanged" class="order-edit-summary__notice" role="status" data-testid="order-edit-quote-changed">
        {{ t.edit_quote_changed }}
      </p>
      <p v-if="amount" class="order-edit-summary__amount" data-testid="order-edit-amount">{{ amount }}</p>
      <div class="order-edit-summary__actions">
        <button
          type="button"
          data-testid="order-edit-validate"
          :disabled="!store.canSubmit"
          :class="{ 'is-disabled': !store.canSubmit }"
          class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default"
          @click="validate"
        >
          <span><strong>{{ buttonLabel }}</strong></span>
        </button>
        <a
          v-if="checkoutFallbackUrl"
          :href="checkoutFallbackUrl"
          target="_top"
          data-testid="order-edit-checkout-fallback"
          class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default"
        >{{ t.edit_checkout_fallback }}</a>
      </div>
    </template>
  </div>
</template>

<style scoped>
.order-edit-summary {
  margin-top: 16px;
  padding-top: 8px;
  border-top: 1px solid var(--bpa-gt-gray-400, #e0e0e0);
}

.order-edit-summary__changes {
  margin: 8px 0;
  padding-left: 20px;
}

.order-edit-summary__amount {
  margin: 12px 0;
  font-weight: 600;
}

.order-edit-summary__error {
  color: #b00020;
}

.order-edit-summary__notice {
  color: #8a4b00;
}

.order-edit-summary__actions {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}
</style>
